/**
 * CSS custom properties shared by the editor preview and (mirrored in PHP) the
 * frontend render. Keeping the variable names in one place is what lets
 * style.scss serve both runtimes unchanged.
 *
 * MIRRORED IN PHP: `SocialIcons/render.php` builds the same variable list.
 */

/**
 * Expand WordPress' preset shorthand into a usable CSS value.
 *
 * Colour controls store palette picks as `var:preset|color|slug`, which is not
 * valid CSS — assigned raw the browser discards the declaration. Mirrors
 * `Helpers::get_ref_color()` in PHP and the identical helper in Timeline.
 *
 * @param {string} value Attribute value.
 * @return {string} CSS value.
 */
export function resolvePresetValue(value) {
  if (typeof value !== 'string' || !value.startsWith('var:')) {
    return value;
  }
  return `var(--wp--${value.slice(4).replace(/\|/g, '--')})`;
}

/**
 * Build the inline custom-property map for a Social Icons wrapper.
 *
 * @param {Object} attributes Block attributes.
 * @return {Object} React style object.
 */
export function getEditorStyles(attributes) {
  const {
    iconSize,
    gap,
    iconColor,
    backgroundColor,
    hoverIconColor,
    hoverBackgroundColor,
  } = attributes;

  const styles = {
    '--brandy-social-icon-size': `${iconSize}px`,
    '--brandy-social-gap': `${gap}px`,
  };

  // Only emit the colour variables that were actually set, so the stylesheet's
  // fallbacks (and brand mode's per-item colour) stay in play otherwise.
  if (iconColor) {
    styles['--brandy-social-color'] = resolvePresetValue(iconColor);
  }
  if (backgroundColor) {
    styles['--brandy-social-bg'] = resolvePresetValue(backgroundColor);
  }
  if (hoverIconColor) {
    styles['--brandy-social-hover-color'] = resolvePresetValue(hoverIconColor);
  }
  if (hoverBackgroundColor) {
    styles['--brandy-social-hover-bg'] = resolvePresetValue(hoverBackgroundColor);
  }

  return styles;
}
